import { Playbook } from '@/types/playbook/playbook';
import { Bot, MessageSquare, Users, TrendingUp, Mail, CheckCircle } from 'lucide-react';

export const proactiveWelcome: Playbook = {
  meta: {
    id: 'proactive-welcome',
    slug: 'proactive-welcome',
    categoryId: 'capture-leads',
    name: 'The Proactive Welcome',
    tagline: 'Greet visitors on your most important pages.',
    description: 'Engage high-intent visitors with perfectly timed, contextual conversations that convert.',
    strategy: 'Engage high-intent visitors on pages like pricing or features with a friendly, proactive offer to talk.',
    benefits: [
      'Increase conversion by 35%',
      'Capture leads at peak interest',
      'Personalized engagement',
      'Reduce bounce rates'
    ],
    targetAudience: ['B2B SaaS', 'High-ticket services', 'Enterprise sales'],
    estimatedSetupTime: '20 minutes',
    difficulty: 'beginner',
    tags: ['lead-capture', 'conversion', 'sales', 'proactive'],
    isFeatured: true,
    relatedPlaybookIds: ['exit-intent-offer', 'demo-scheduler']
  },
  seo: {
    metaTitle: 'Proactive Welcome Playbook - Convert Visitors with Timely Engagement',
    metaDescription: 'Increase conversions by 35% with Genie-powered proactive welcome messages. Learn the best practices for engaging high-intent visitors.',
    keywords: ['proactive engagement', 'lead capture', 'conversion optimization', 'visitor engagement', 'sales automation']
  },
  demoConfig: {
    triggerType: 'delayed',
    delay: 2000,
    message: "Hi there! I noticed you're checking out our pricing. I can help you find the perfect plan for your needs. What brings you here today?",
    position: 'bottom-right'
  },
  demoScript: [
    { speaker: 'agent', message: "Hi! I see you're looking at our pricing. Can I help find the right plan?" },
    { speaker: 'user', message: "I need something for a team of 20" },
    { speaker: 'agent', message: "Perfect! Our Business plan would be ideal. It's $299/month for up to 25 users." },
    { speaker: 'user', message: "What features does that include?" },
    { speaker: 'agent', message: "You get unlimited genies, advanced analytics, and priority support. Want me to schedule a demo?" }
  ],
  templateConfig: {
    firstMessage: "Hi there! I noticed you're exploring our [PAGE]. I'm here to help answer any questions. What brings you here today?",
    systemPrompt: "You are a friendly sales Genie. Greet visitors warmly and help them find the right solution. Focus on understanding their needs before presenting options.",
    tools: ['calendar_booking', 'email_capture'],
    conversationStarters: [
      "Looking for the right plan?",
      "Need help comparing options?",
      "Want to see a demo?"
    ]
  },
  flowSteps: [
    {
      type: 'user',
      icon: Users,
      genieIcon: 'user',
      label: 'High-intent visitor arrives',
      description: 'Visitor lands on pricing, features, or demo page showing buying intent'
    },
    {
      type: 'genie',
      icon: Bot,
      genieIcon: 'genie-lamp',
      label: 'Contextual greeting',
      description: 'Personalized welcome based on page content and visitor behavior'
    },
    {
      type: 'action',
      icon: MessageSquare,
      genieIcon: 'target',
      label: 'Needs discovery',
      description: 'Asks qualifying questions to understand requirements and use case'
    },
    {
      type: 'action',
      icon: TrendingUp,
      genieIcon: 'target',
      label: 'Solution matching',
      description: 'Recommends specific plans or features based on stated needs'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'mail',
      label: 'Lead capture',
      description: 'Collects contact information and schedules follow-up or demo'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Qualified lead generated',
      description: 'Hot lead with context passed to sales team for immediate follow-up'
    }
  ],
  metrics: {
    conversionRate: '35%',
    responseTime: '2 seconds',
    satisfactionScore: '4.7/5',
    costSavings: '$30k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'DataFlow Inc',
      authorName: 'Michael Rodriguez',
      authorTitle: 'VP of Sales',
      quote: 'The Proactive Welcome increased our demo bookings by 40%. Visitors love the immediate, personalized assistance.',
      metrics: '40% more demos booked'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Timing Your Welcome Messages',
      description: 'Research-backed guide on optimal trigger timing for different page types',
      url: '/docs/welcome-timing-guide'
    },
    {
      type: 'template',
      title: 'Conversation Starters Pack',
      description: '50+ proven opening messages for different scenarios',
      url: '/templates/conversation-starters'
    }
  ]
};